import {
    AfterViewInit,
    Directive,
    ElementRef,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
} from '@angular/core';

/**
 * Highlights occurrences of a search term inside already-rendered message
 * markup (SD-6742). Applied to the messages container; when a conversation is
 * opened from a search, this marks the matched words AND scrolls the first
 * match into view with a brief flash on its bubble.
 *
 * Why a TreeWalker instead of the `highlight` pipe: message bubbles render
 * markdown to real HTML (links, code, lists). Running a regex over that HTML
 * string would corrupt tags/attributes. Walking TEXT NODES only — and skipping
 * code/pre — wraps matches safely without ever touching markup.
 */
@Directive({
    selector: '[appGableMessageHighlight]',
    standalone: true,
})
export class GableMessageHighlightDirective implements AfterViewInit, OnChanges, OnDestroy {
    /** The active search term. Empty/whitespace clears any existing marks.
     *  Named to match the selector so no @Input alias is needed (no-input-rename). */
    @Input() appGableMessageHighlight: string | null = null;

    private _raf: number | null = null;

    constructor(
        private readonly _el: ElementRef<HTMLElement>,
        private readonly _zone: NgZone,
    ) {}

    ngAfterViewInit(): void {
        this._schedule();
    }

    ngOnChanges(): void {
        this._schedule();
    }

    ngOnDestroy(): void {
        this._cancel();
    }

    /**
     * Defer to the next frame so the markdown `innerHTML` bindings are in the
     * DOM before we walk them. Runs outside Angular — pure DOM work, no state.
     */
    private _schedule(): void {
        this._cancel();
        this._zone.runOutsideAngular(() => {
            this._raf = requestAnimationFrame(() => {
                this._raf = null;
                this._apply();
            });
        });
    }

    private _cancel(): void {
        if (this._raf !== null) {
            cancelAnimationFrame(this._raf);
            this._raf = null;
        }
    }

    private _apply(): void {
        const root = this._el.nativeElement;
        this._clear(root);

        const term = (this.appGableMessageHighlight ?? '').trim();
        if (term === '') {
            return;
        }

        // Highlight the full literal query string ("unit 113"), not each word —
        // the server matches the exact phrase for multi-word terms, so the
        // highlight mirrors exactly what was searched (no per-word noise).
        const wrapped = this._highlight(root, term.toLowerCase());
        if (wrapped > 0) {
            this._scrollToFirst(root);
        }
    }

    /** Unwrap any marks WE added and re-join the split text. */
    private _clear(root: HTMLElement): void {
        const marks = root.querySelectorAll('mark.search-highlight');
        marks.forEach((m) => m.replaceWith(document.createTextNode(m.textContent ?? '')));
        if (marks.length > 0) {
            root.normalize();
        }
    }

    /** Wrap every case-insensitive occurrence of `lc` (the full query) in text nodes. */
    private _highlight(root: HTMLElement, lc: string): number {
        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
            acceptNode: (node) => {
                const parent = node.parentElement;
                if (parent === null) {
                    return NodeFilter.FILTER_REJECT;
                }
                // Never highlight inside code/pre (would mangle snippets) or
                // inside a mark we already placed.
                if (parent.closest('code, pre, mark, script, style') !== null) {
                    return NodeFilter.FILTER_REJECT;
                }
                return (node.nodeValue ?? '').toLowerCase().includes(lc)
                    ? NodeFilter.FILTER_ACCEPT
                    : NodeFilter.FILTER_REJECT;
            },
        });

        const targets: Text[] = [];
        let current: Node | null;
        while ((current = walker.nextNode()) !== null) {
            targets.push(current as Text);
        }

        let count = 0;
        for (const textNode of targets) {
            count += this._wrap(textNode, lc);
        }
        return count;
    }

    private _wrap(textNode: Text, lc: string): number {
        const text = textNode.nodeValue ?? '';
        const lower = text.toLowerCase();
        const frag = document.createDocumentFragment();
        let cursor = 0;
        let idx = lower.indexOf(lc, cursor);
        let count = 0;

        while (idx !== -1) {
            if (idx > cursor) {
                frag.appendChild(document.createTextNode(text.slice(cursor, idx)));
            }
            const mark = document.createElement('mark');
            mark.className = 'search-highlight';
            mark.textContent = text.slice(idx, idx + lc.length);
            frag.appendChild(mark);
            count++;
            cursor = idx + lc.length;
            idx = lower.indexOf(lc, cursor);
        }

        if (cursor < text.length) {
            frag.appendChild(document.createTextNode(text.slice(cursor)));
        }
        textNode.replaceWith(frag);
        return count;
    }

    /** Center the first match and flash its bubble so the eye finds it. */
    private _scrollToFirst(root: HTMLElement): void {
        const first = root.querySelector<HTMLElement>('mark.search-highlight');
        if (first === null) {
            return;
        }
        first.scrollIntoView({ block: 'center', behavior: 'smooth' });
        const bubble = first.closest<HTMLElement>('.message');
        if (bubble !== null) {
            bubble.classList.remove('gable-match-flash');
            // Force reflow so re-adding the class restarts the animation.
            void bubble.offsetWidth;
            bubble.classList.add('gable-match-flash');
        }
    }
}
